import { selectIsCombinedAddToListFFOn } from 'core/userContext/selectors';
import merge from 'lodash/merge';
import { useIsModernizedSegmentationPageEnabled } from 'modules/segmentation/hooks';
import { StyledLeftTooltip } from 'modules/workflowNodeEditors/EditorBody.styled';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { NODE_CLASS_TO_TYPE_MAP, WORKFLOW_ACTIONS } from 'modules/workflows/constants';
import {
  closeNode,
  getIsNodeEditorReadOnly,
  updateNode as updateNodeAction,
} from 'modules/workflows/ducks';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
import { AddContactNodeData, RemoveContactNodeData } from 'modules/workflows/types';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormField } from 'ui/formField';
import { Link } from 'ui/links';
import { CreateListButton } from '../../editorComponents/CreateListButton';
import { EditorBaseProps } from '../../types';
import { OnChangeHandler, OnMultiChangeHandler } from '../formElements';
import {
  LIST_MEMBERSHIP_EDITOR_TEST_ID,
  LIST_MEMBERSHIP_SELECT_ID,
  MEMBERSHIP_ACTION_OPTIONS,
  VIEW_SELECTED_LIST_LINK_ID,
} from './constants';
import { ListMembershipSelect } from './ListMembershipSelect';
import {
  ListSelectWrapper,
  StyledFormLabel,
  StyledRadioList,
} from './ModifyListMembershipEditor.styles';

export type ListMembershipNodeData = AddContactNodeData | RemoveContactNodeData;
export type ListMembershipEditorProps = EditorBaseProps<ListMembershipNodeData>;
type ListMembershipNode = ListMembershipEditorProps['node'];
type PartialNode = Partial<
  Omit<ListMembershipNode, 'nodeData'> & { nodeData: Partial<ListMembershipNodeData> }
>;

export const ModifyListMembershipEditor = (props: ListMembershipEditorProps) => {
  const { node: originalNode, nodeId, clonedFromNodeData } = props;
  const dispatch = useDispatch();
  const [node, setNode] = useState<ListMembershipNode>(originalNode || {});
  const [isDirty, setIsDirty] = useState(false);
  const isCombinedAddToListFFOn = useSelector(selectIsCombinedAddToListFFOn);

  const { nodeData } = node;
  const { nodeClass, emailListId: listId, emailListIds = [] } = nodeData as ListMembershipNodeData;
  const isAddContactNode = nodeClass === WORKFLOW_ACTIONS.ADD_CONTACT;

  const useMultiSelect = isCombinedAddToListFFOn;

  const { isCloneWarningBannerShown, cloneErrors, cloneDisplayValues } = useDisplayCloneErrors({
    clonedFromNodeData,
    fieldsToValidate:
      useMultiSelect && !!clonedFromNodeData?.emailListIds
        ? { emailListIds: { isValid: emailListIds && emailListIds?.length > 0 } }
        : { emailListId: { isValid: listId, displayValue: clonedFromNodeData?.resource?.name } },
    nodeClass,
  });

  const isModernizedSegmentationPageEnabled = useIsModernizedSegmentationPageEnabled();

  const updateNode = (data: PartialNode, isMulti?: boolean) => {
    setNode(isMulti ? { ...node, nodeData: data.nodeData } : merge({}, node, data));
    setIsDirty(true);
  };

  const updateNodeData = (
    data: Partial<ListMembershipNodeData>,
    isMulti?: boolean,
    isNewList?: boolean,
  ) => {
    const multiData = {
      ...nodeData,
      ...data,
      emailListIds: isNewList
        ? [...(nodeData.emailListIds ?? []), ...((data.emailListIds ?? []) as number[])]
        : data.emailListIds,
    };
    updateNode({ nodeData: isMulti ? multiData : merge({}, nodeData, data) }, isMulti);
  };

  const updateNodeClass = (value: string) =>
    updateNode({ type: NODE_CLASS_TO_TYPE_MAP[value], nodeData: { nodeClass: value } });

  const handleListSelect: OnChangeHandler = (idStr, { option: { label } }) => {
    const id = Number(idStr);

    /**
     * After list creation, the select incorrectly triggers an `onChange` event
     * since the incoming listId has changed.  In certain conditions, this
     * causes the select name to be incorrect.
     * Checking for the id prevents that from happening.
     */
    if (id !== listId) {
      updateNodeData({ emailListId: id, resource: { name: label as string } });
    }
  };

  const handleMultiListSelect: OnMultiChangeHandler = (ids: string[]) => {
    const removedSingleListId = !!listId && !ids.includes(String(listId));

    const emailListIds = ids.map((id) => Number(id));

    updateNodeData({ emailListIds, ...(removedSingleListId ? { emailListId: null } : {}) }, true);
  };

  const uniqueMultiListIds = useMemo(() => {
    const numericIds = [...(listId ? [listId] : []), ...(emailListIds ?? [])];
    const uniqueNumericIds = [...new Set(numericIds)];
    return uniqueNumericIds.map(String);
  }, [listId, emailListIds]);

  /**
   * Updates the node globally
   */
  const handleUpdate = () => {
    dispatch(
      updateNodeAction({ type: node.type, nodeData: node.nodeData } as ListMembershipNode, nodeId),
    );
    dispatch(closeNode());
  };

  /** read only state related variables */
  const isTileEditorReadOnly = useSelector(getIsNodeEditorReadOnly);

  return (
    <NodeEditor
      {...props}
      dataTest={LIST_MEMBERSHIP_EDITOR_TEST_ID}
      onUpdate={handleUpdate}
      getHasUnsavedChanges={() => isDirty}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      <FormField>
        <StyledFormLabel>Action</StyledFormLabel>
        <StyledRadioList
          name="set-membership-action"
          onSelect={updateNodeClass}
          options={MEMBERSHIP_ACTION_OPTIONS.map((option) => ({
            ...option,
            isDisabled: isTileEditorReadOnly,
          }))}
          value={nodeClass}
        />
      </FormField>
      <FormField>
        <StyledFormLabel>Audience List</StyledFormLabel>
        <ListSelectWrapper>
          <StyledLeftTooltip
            isEnabled={isTileEditorReadOnly}
            tooltipContent={nodeData?.resource?.name ?? ''}
          >
            {useMultiSelect ? (
              <ListMembershipSelect
                id={LIST_MEMBERSHIP_SELECT_ID}
                data-test={LIST_MEMBERSHIP_SELECT_ID}
                onChange={handleMultiListSelect}
                value={uniqueMultiListIds}
                isError={cloneErrors?.emailListIds}
                placeholder={cloneDisplayValues?.emailListIds}
                isReadOnly={isTileEditorReadOnly}
                isMulti
              />
            ) : (
              <ListMembershipSelect
                id={LIST_MEMBERSHIP_SELECT_ID}
                data-test={LIST_MEMBERSHIP_SELECT_ID}
                onChange={handleListSelect}
                value={`${listId || ''}`}
                isError={cloneErrors?.emailListId}
                placeholder={cloneDisplayValues?.emailListId}
                isReadOnly={isTileEditorReadOnly}
              />
            )}
          </StyledLeftTooltip>
          {isAddContactNode && !isTileEditorReadOnly && (
            <CreateListButton
              isMulti={useMultiSelect}
              onListCreate={(data) => updateNodeData(data, useMultiSelect, true)}
            />
          )}
        </ListSelectWrapper>
      </FormField>
      {!useMultiSelect && listId && (
        <Link
          hasOpenNewTabIcon
          data-test={VIEW_SELECTED_LIST_LINK_ID}
          href={`/segmentation?emailListId=${listId}`}
          target="_blank"
          useReactRouter={isModernizedSegmentationPageEnabled}
          rel={isModernizedSegmentationPageEnabled ? 'noopener noreferrer' : undefined}
        >
          View
        </Link>
      )}
    </NodeEditor>
  );
};
